import { createApi } from '@reduxjs/toolkit/query/react';
import { getConfig } from '@/config';
import { clientKeyBaseQuery } from './clientKeyBaseQuery';
import { CATEGORIES_API_TAG_LIST, CATEGORIES_API_TAGS } from './constants';
// ---------------------------------------------------------------------------
// API slice
// ---------------------------------------------------------------------------
/**
 * Dedicated RTK Query API slice for category mega-menu CRUD operations.
 *
 * This slice uses its own `fetchBaseQuery` because the Categories API
 * authenticates via `client-key` — not the Bearer-token auth that `ccApi`
 * injects via `prepareHeaders`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
/** Shared invalidation for mutations that modify a specific custom category. */
const invalidateCustomCategoryAndMenu = (_result, _error, { megaMenuName, id }) => [
    { type: CATEGORIES_API_TAGS.CustomCategory, id: `${megaMenuName}/${id}` },
    { type: CATEGORIES_API_TAGS.MegaMenuCategories, id: megaMenuName },
];
export const categoriesApi = createApi({
    reducerPath: 'categoriesApi',
    baseQuery: clientKeyBaseQuery,
    tagTypes: CATEGORIES_API_TAG_LIST,
    endpoints: (builder) => ({
        getCategoryMegaMenus: builder.query({
            query: ({ clientKey }) => ({
                url: getConfig().categoryApiUrl,
                method: 'GET',
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            transformResponse: (response) => response.items,
            providesTags: [CATEGORIES_API_TAGS.MegaMenus],
        }),
        getMegaMenuCategories: builder.query({
            query: ({ clientKey, id, showHidden }) => ({
                url: `${getConfig().categoryApiUrl}/${id}/categories${showHidden ? '?showHidden=true' : ''}`,
                method: 'GET',
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            transformResponse: (response) => response.items,
            providesTags: (_result, _error, { id }) => [{ type: CATEGORIES_API_TAGS.MegaMenuCategories, id }],
        }),
        createMegaMenuCategories: builder.mutation({
            query: ({ clientKey, name, categories }) => {
                const formData = new FormData();
                formData.append('name', name);
                formData.append('categories', categories);
                return {
                    url: getConfig().categoryApiUrl,
                    method: 'POST',
                    body: formData,
                    headers: {
                        Authorization: `client-key ${clientKey}`,
                    },
                    // Let the browser set Content-Type with boundary for FormData
                    formData: true,
                };
            },
            transformResponse: (_response, meta) => { var _a, _b; return (_b = (_a = meta === null || meta === void 0 ? void 0 : meta.response) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : 200; },
            transformErrorResponse: (response) => response.status,
            invalidatesTags: [CATEGORIES_API_TAGS.MegaMenus],
        }),
        deleteMegaMenu: builder.mutation({
            query: ({ clientKey, id }) => ({
                url: `${getConfig().categoryApiUrl}/${id}`,
                method: 'DELETE',
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            invalidatesTags: [CATEGORIES_API_TAGS.MegaMenus],
        }),
        getCanonicalCategory: builder.query({
            query: ({ clientKey, megaMenuName, id }) => ({
                url: `${getConfig().categoryApiUrl}/${megaMenuName}/categories/${id}/canonical`,
                method: 'GET',
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            transformResponse: (response) => response.item,
        }),
        getCustomCategory: builder.query({
            query: ({ clientKey, megaMenuName, id }) => ({
                url: `${getConfig().categoryApiUrl}/${megaMenuName}/categories/${id}/custom`,
                method: 'GET',
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            transformResponse: (response) => response === null || response === void 0 ? void 0 : response.item,
            providesTags: (_result, _error, { megaMenuName, id }) => [
                { type: CATEGORIES_API_TAGS.CustomCategory, id: `${megaMenuName}/${id}` },
            ],
        }),
        updateCustomCategory: builder.mutation({
            query: ({ clientKey, megaMenuName, id, data, }) => ({
                url: `${getConfig().categoryApiUrl}/${megaMenuName}/categories/${id}/custom`,
                method: 'PATCH',
                body: data,
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            invalidatesTags: invalidateCustomCategoryAndMenu,
        }),
        createCustomCategory: builder.mutation({
            query: ({ clientKey, megaMenuName, id, data, }) => ({
                url: `${getConfig().categoryApiUrl}/${megaMenuName}/categories`,
                method: 'POST',
                body: Object.assign({ id }, data),
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            invalidatesTags: (_result, _error, { megaMenuName }) => [
                { type: CATEGORIES_API_TAGS.MegaMenuCategories, id: megaMenuName },
            ],
        }),
        deleteCustomCategory: builder.mutation({
            query: ({ clientKey, megaMenuName, id }) => ({
                url: `${getConfig().categoryApiUrl}/${megaMenuName}/categories/${id}/custom`,
                method: 'DELETE',
                headers: {
                    Authorization: `client-key ${clientKey}`,
                },
            }),
            invalidatesTags: invalidateCustomCategoryAndMenu,
        }),
    }),
});
export const { useGetCategoryMegaMenusQuery, useLazyGetCategoryMegaMenusQuery, useGetMegaMenuCategoriesQuery, useLazyGetMegaMenuCategoriesQuery, useCreateMegaMenuCategoriesMutation, useDeleteMegaMenuMutation, useGetCanonicalCategoryQuery, useLazyGetCanonicalCategoryQuery, useGetCustomCategoryQuery, useLazyGetCustomCategoryQuery, useUpdateCustomCategoryMutation, useCreateCustomCategoryMutation, useDeleteCustomCategoryMutation, } = categoriesApi;
